import {InfiniteData, QueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {ConversationMessage, ConversationMessagesList} from '../types'

/**
 * Returns the id of the message the current user is allowed to delete, or null when there is
 * none. Mirrors the backend rule: only the conversation's last *non-deleted* message can be
 * deleted, and only by its sender. Deletion is chained — once the last message is gone the
 * previous one becomes deletable by its own author.
 *
 * `messages` must be ordered oldest-to-newest, as returned by `useConversationMessages`.
 */
export const getDeletableMessageId = (messages: ConversationMessage[], currentUserId?: string): string | null => {
    if (!currentUserId) return null

    for (let index = messages.length - 1; index >= 0; index--) {
        const message = messages[index]
        if (message.deletedAt) continue
        return message.senderId === currentUserId ? message.id : null
    }

    return null
}

/**
 * Flags a message as deleted in every cached page of the conversation, mirroring the backend
 * soft delete: the message stays in the list with an empty body and a `deletedAt` timestamp so
 * the UI renders the "message deleted" placeholder in its place.
 *
 * Neither the DELETE response (204) nor the socket payload carries the deletion timestamp, so
 * the local clock is used — the value is only ever read as a flag, the bubble keeps showing
 * the message's own `createdAt`.
 */
const markMessageAsDeletedInCache = (queryClient: QueryClient, conversationId: string, messageId: string) => {
    const deletedAt = new Date().toISOString()

    // Prefix match: the messages query key also carries the page size
    queryClient.setQueriesData(
        {queryKey: [QUERY_KEYS.CONVERSATION_MESSAGES, conversationId], exact: false},
        (oldData: InfiniteData<ConversationMessagesList> | undefined) => {
            if (!oldData) return oldData

            let hasChanged = false
            const updatedPages = oldData.pages.map(page => {
                if (!page.data.some(message => message.id === messageId && !message.deletedAt)) return page

                hasChanged = true
                return {
                    ...page,
                    data: page.data.map(message =>
                        message.id === messageId ? {...message, message: '', deletedAt} : message
                    )
                }
            })

            return hasChanged ? {...oldData, pages: updatedPages} : oldData
        }
    )
}

/**
 * Reconciles every cache touched by a message deletion, whether it originated from this client
 * (mutation) or from another member (socket event).
 *
 * Besides the message itself the backend also soft-deletes its read receipts and the related
 * "Nuovo messaggio" notification with its recipients, so the conversations list (preview,
 * urgency, unread dot) and the notification list/counter are refetched rather than patched.
 */
export const applyConversationMessageDeletion = (
    queryClient: QueryClient,
    conversationId: string,
    messageId: string
) => {
    markMessageAsDeletedInCache(queryClient, conversationId, messageId)

    // Prefix invalidation: refreshes every conversations list (any patient and internal)
    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CONVERSATIONS]})
    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NOTIFICATIONS]})
    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NOTIFICATION_COUNT]})
}
